import { useSession, useUser } from "@clerk/clerk-react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
  Spinner,
} from "@heroui/react";
import { Phone, ClipboardText, User, Clock } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { API_URL } from "constants/index";
import { useState } from "react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";

interface PostCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactId: number;
  contactName?: string;
  phoneNumber: string;
  durationSeconds?: number;
  onCallLogged?: () => void;
}

/**
 * Modal that opens after a call to ask "Wat heb je met deze persoon gesproken?"
 * and logs the call with notes to the contact's call history.
 */
export const PostCallModal = ({
  isOpen,
  onClose,
  contactId,
  contactName,
  phoneNumber,
  durationSeconds,
  onCallLogged,
}: PostCallModalProps) => {
  const { session } = useSession();
  const { user } = useUser();
  const [notes, setNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Format duration as MM:SS
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Format phone number for display
  const formatPhone = (phone: string) => {
    return phone.replace(/(\+32)(\d{3})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4 $5");
  };

  const handleSaveCallLog = async () => {
    if (!user?.id) {
      toast.error("Kan gebruiker niet identificeren");
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/contact/call-logs/log-phone-call`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contactId,
            phoneNumber,
            createdByUserId: user.id,
            durationSeconds,
            notes: notes.trim() || undefined,
          }),
        },
        session
      );

      if (!response.ok) {
        throw new Error("Failed to log call");
      }

      toast.success("Gesprek gelogd");
      onCallLogged?.();
      handleClose();
    } catch (error) {
      console.error("Error logging call:", error);
      toast.error("Fout bij het loggen van het gesprek");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setNotes("");
    onClose();
  };

  const handleSkip = () => {
    // Still log the call but without notes
    handleSaveCallLog();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <ClipboardText weight="duotone" size={24} className="text-moss-primary" />
            <span>Gesprek loggen</span>
          </div>
        </ModalHeader>

        <ModalBody>
          <div className="flex flex-col gap-4">
            {/* Call summary */}
            <div className="flex items-center gap-4 p-4 bg-moss-primary/5 rounded-lg">
              <div className="p-3 rounded-full bg-moss-primary/10">
                <Phone weight="duotone" size={24} className="text-moss-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <User weight="duotone" size={16} className="text-neutral-secondary-mid" />
                  <span className="font-medium text-neutral-primary">
                    {contactName || "Onbekend contact"}
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-1 text-sm text-neutral-secondary-mid">
                  <span>{formatPhone(phoneNumber)}</span>
                  {durationSeconds !== undefined && durationSeconds > 0 && (
                    <div className="flex items-center gap-1">
                      <Clock size={14} />
                      <span>{formatDuration(durationSeconds)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Notes input */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-neutral-primary">
                Wat heb je met deze persoon gesproken?
              </label>
              <Textarea
                placeholder="Voeg hier je notities toe over het gesprek..."
                value={notes}
                onValueChange={setNotes}
                minRows={4}
                maxRows={8}
                classNames={{
                  input: "text-neutral-primary placeholder:text-neutral-secondary-light",
                  inputWrapper: "bg-white shadow-form-element data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
                }}
              />
              <p className="text-xs text-neutral-secondary-light">
                Notities zijn optioneel maar helpen bij het bijhouden van contacthistorie.
              </p>
            </div>
          </div>
        </ModalBody>

        <ModalFooter className="flex justify-between">
          <ButtonNew
            variant="secondaryNeutral"
            label="Overslaan"
            onClick={handleSkip}
            isDisabled={isSaving}
          />
          <div className="flex gap-2">
            <ButtonNew
              variant="secondaryNeutral"
              label="Annuleren"
              onClick={handleClose}
              isDisabled={isSaving}
            />
            <ButtonNew
              variant="primaryMoss"
              label={isSaving ? "Opslaan..." : "Opslaan"}
              icon={isSaving ? <Spinner size="sm" /> : <ClipboardText size={16} />}
              onClick={handleSaveCallLog}
              isDisabled={isSaving || !notes.trim()}
            />
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PostCallModal;
